import { map, switchMap, Observable, of, tap, take } from 'rxjs';

import {
  C_Save_Operation_Status,
  GetConfigDataQuery,
  SaveConfigDataMutation,
  Wa_SaveConfigInput,
} from '../../../graphql/generatedModel';
import { gqlClient } from '../../../graphql/graphqlRequest';
import { storageHelper } from '../../helpers/storage';
import { sessionstorage } from '../../helpers/storage/session.keys';
import { getCompanyConfig, saveConfigData } from './gql/getConfigs.gql';
import { responseHandler } from '../../responseHandler/responseHandler';
import { localeFormatterHelper } from '../../helpers/formatter/localeFormatter.helper.ts';
import { readStoredCompanyGuid } from '../tenant/tenant.helper.ts';
class CompanyConfig {
  private storageKey: sessionstorage = 'companyConfig';
  private storageConfig:
    | { data?: TCompanyConfigs; updatedAt?: number; companyGuid?: string }
    | undefined = storageHelper.session.getItem(this.storageKey);

  /**
   * The company this cache belongs to (WA-3495).
   *
   * `getConfigData` is per-tenant to its core — VAT/tax system, decimal places,
   * counters, the company address printed on reports, and `currentUser.company`,
   * which the top bar shows. The cache used to be invalidated only by calendar
   * day, so after a support company switch the tab kept serving the PREVIOUS
   * tenant's configuration (and its name) for the rest of the day.
   *
   * The stamp is compared against `activeCompanyGuid`, written by the tenant
   * guard. Both sides fall back to `''`, which keeps two cases working: a cache
   * written before this field existed, and any context where the guard has not
   * run (tests, `/login`) — there `'' === ''` preserves the old day-based
   * behaviour instead of forcing a needless refetch.
   */
  private currentCompanyGuid(): string {
    return readStoredCompanyGuid();
  }

  async getConfigsRaw(forceRun?: boolean): Promise<TCompanyConfigs> {
    return new Promise((res) => {
      this.getConfigs(forceRun)
        .pipe(take(1))
        .subscribe((v) => res(v));
    });
  }
  getConfigs(forceRun?: boolean): Observable<TCompanyConfigs> {
    // Update session storage value if it was last updated not today
    const isActualCacheData =
      localeFormatterHelper.localizedDate().getDate() ===
      localeFormatterHelper.localizedDate(this.storageConfig?.updatedAt ?? 0).getDate();
    // A config cached for a different company is not stale — it is wrong.
    const isSameCompany = (this.storageConfig?.companyGuid ?? '') === this.currentCompanyGuid();
    const useCache = this.storageConfig?.data && !forceRun && isActualCacheData && isSameCompany;

    return useCache
      ? of(this.storageConfig!.data!)
      : gqlClient(getCompanyConfig).pipe(
          map((res) => {
            const typedRes = res as GetConfigDataQuery;
            this.storageConfig = {
              data: typedRes?.wawiAssist?.getConfigData,
              updatedAt: localeFormatterHelper.localizedDate().getTime(),
              companyGuid: this.currentCompanyGuid(),
            };
            storageHelper.session.setItem(this.storageKey, this.storageConfig);
            return this.storageConfig.data as TCompanyConfigs;
          }),
          responseHandler<TCompanyConfigs>({
            // An EMPTY OBJECT, never `undefined` (WA-3750). On the session-expiry
            // branch below this value is what the stream emits, and ~10 call sites
            // dereference it straight away (`v.currentUser`, `v.countryId`,
            // `({ id }) => …`, `config.nextDeliveryDate`, …). Emitting `undefined`
            // turned every one of them into a TypeError during logout — the crash
            // Sentry WAWIASSIST-23B filed from `root.layout.tsx` (`Cannot read
            // properties of undefined (reading 'currentUser')`). `{}` keeps each
            // field resolving to `undefined` so consumers fall back to their
            // defaults for the moment the app spends redirecting to /login, and
            // still lets getConfigsRaw's promise resolve rather than hang.
            errorReturnType: {} as TCompanyConfigs,
            customErrorHandler: (e) => {
              // A genuine session expiry is a login-lifecycle event, not a bug:
              // gqlClient has already flagged it and redirected to login. Do NOT
              // re-throw here — a fresh Error would escape uncaught WITHOUT the
              // __sentryIgnore/__sessionExpired flags gqlClient set, bypassing
              // both responseHandler's captureMessage guard and main.tsx's
              // beforeSend, which auto-files Jira tickets (WA-3598 — same family
              // as WA-3514…WA-3538 / WA-3546). Returning here lets the stream
              // emit errorReturnType so getConfigsRaw's promise resolves instead
              // of hanging on an unhandled error.
              if (e.__sessionExpired) return;
              throw new Error(`Error in getConfigData request: ${e.message}`);
            },
          }),
        );
  }
  getCachedConfigs(): TCompanyConfigs | undefined {
    // Same tenant check as `getConfigs()` — this is the synchronous seam the top
    // bar's `UserProfile` seeds from, and it only ever refetches when this
    // returns undefined. Without the check it would paint the previous
    // company's name and never correct itself (the WA-3495 symptom).
    if ((this.storageConfig?.companyGuid ?? '') !== this.currentCompanyGuid()) return undefined;
    return this.storageConfig?.data ? { ...this.storageConfig?.data } : undefined;
  }
  setShowDoughInLiterInProductionLists(v: boolean): void {
    this.storageConfig = {
      ...(this.storageConfig || {}),
      companyGuid: this.currentCompanyGuid(),
      data: {
        ...(this.storageConfig?.data || ({} as TCompanyConfigs)),
        isDoughInLitersInProductionReports: v,
      },
    };
    storageHelper.session.setItem(this.storageKey, this.storageConfig);
  }
  saveConfigData(params: Wa_SaveConfigInput): Observable<any> {
    return (
      gqlClient(saveConfigData, { dataToSave: params }) as Observable<SaveConfigDataMutation>
    ).pipe(
      map((res) => res.wawiAssist?.saveConfigData as TSaveConfigData),
      responseHandler<TSaveConfigData>({
        success: () => 'settings.configuration_saved',
        customErrorHandler: () => 'settings.configuration_not_saved',
        errorReturnType: {
          status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED,
        },
      }),
      switchMap((res) => {
        const out = {
          updateStorageValue: res.status === C_Save_Operation_Status.SOS1_DATA_CHANGED,
        };
        // Update of fields isDeliveryOn... require fetch new value for nextDeliveryDate field
        const fieldsRequireReFetch = [
          'isDeliveryOnMon',
          'isDeliveryOnTue',
          'isDeliveryOnWed',
          'isDeliveryOnThu',
          'isDeliveryOnFri',
          'isDeliveryOnSat',
          'isDeliveryOnSun',
        ];
        const isFetchRequired = Object.keys(params).some((field) =>
          fieldsRequireReFetch.includes(field),
        );
        if (isFetchRequired) {
          return this.getConfigs(true).pipe(
            map(() => {
              // storage value is updated at this.getConfigs method
              out.updateStorageValue = false;
              return out;
            }),
          );
        }

        return of(out);
      }),
      tap((res) => {
        if (res.updateStorageValue) {
          this.storageConfig = {
            ...(this.storageConfig ?? {}),
            companyGuid: this.currentCompanyGuid(),
            data: {
              ...(this.storageConfig?.data ?? {}),
              ...(params as TCompanyConfigs),
            },
          };
          storageHelper.session.setItem(this.storageKey, this.storageConfig);
        }
      }),
    );
  }
}

export const companyConfigService = new CompanyConfig();

export type TCompanyConfigs = NonNullable<
  NonNullable<GetConfigDataQuery['wawiAssist']>['getConfigData']
>;

export type TSaveConfigData = NonNullable<
  NonNullable<SaveConfigDataMutation['wawiAssist']>['saveConfigData']
>;
